/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback, useRef } from "react";
import { Loading, Error, useTranslate } from "react-admin";
import AccessDenied from "layout/AccessDenied";
import { makeStyles } from "@material-ui/core/styles";
import {
  Paper,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  CircularProgress,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import AddIcon from "@material-ui/icons/Add";
import { getCategorySetupInitialData } from "services/setting";
import ModelUriQueryString from "models/uriQueryString";
import DetailPanel from "./Dialog/DepartmentCategory";
import { permissionName } from "utils/constants";

const useStyles = makeStyles((theme) => ({
  tabPanel: { width: "100%", margin: "0 20px" },
  container: {
    display: "flex",
    gap: theme.spacing(2),
    minHeight: "calc(100vh - 220px)",
  },
  listPanel: {
    width: "32%",
    minWidth: 320,
    display: "flex",
    flexDirection: "column",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 16,
    overflow: "hidden",
    backgroundColor: "#fbfcff",
  },
  listHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: "#ffffff",
  },
  listTitle: {
    fontWeight: 700,
    color: "#1f2a44",
  },
  addButton: {
    width: 36,
    height: 36,
    borderRadius: 10,
    background: "linear-gradient(135deg, #5b50f6 0%, #4b67ff 100%)",
    color: "#fff",
    "&:hover": {
      background: "linear-gradient(135deg, #4d43ea 0%, #3f5cf4 100%)",
    },
  },
  searchBox: {
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: "#ffffff",
  },
  summaryBox: {
    display: "flex",
    justifyContent: "space-around",
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: "#f9fafc",
  },
  summaryItem: {
    textAlign: "center",
  },
  summaryValue: {
    fontSize: "1.1rem",
    fontWeight: 700,
    color: "#1f2a44",
  },
  summaryLabel: {
    fontSize: "0.75rem",
    fontWeight: 600,
    color: "#6b7894",
  },
  searchInput: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 12,
      backgroundColor: "#f4f6fb",
    },
  },
  listBody: {
    overflowY: "auto",
    padding: theme.spacing(1),
    flex: 1,
  },
  listItem: {
    padding: theme.spacing(1.5),
    borderRadius: 14,
    cursor: "pointer",
    border: "1px solid transparent",
    marginBottom: theme.spacing(1),
    width: "100%",
    textAlign: "left",
    backgroundColor: "#fff",
    appearance: "none",
    font: "inherit",
    transition: "all 0.2s ease",
    "&:hover": {
      borderColor: "#cfd8ff",
      boxShadow: "0 6px 20px rgba(65, 88, 208, 0.08)",
    },
  },
  listItemSelected: {
    borderColor: "#aebcff",
    backgroundColor: "#eef1ff",
    boxShadow: "0 10px 24px rgba(65, 88, 208, 0.12)",
  },
  itemCode: {
    fontSize: "0.72rem",
    fontWeight: 700,
    color: "#6b7894",
    marginBottom: theme.spacing(0.5),
    textTransform: "uppercase",
  },
  itemTitleRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: theme.spacing(1),
  },
  itemTitle: {
    fontWeight: 700,
    color: "#1f2a44",
  },
  itemSubtext: {
    marginTop: theme.spacing(0.75),
    color: "#72809d",
    fontSize: "0.86rem",
  },
  statusChip: {
    height: 22,
    fontWeight: 700,
    textTransform: "lowercase",
  },
  activeChip: {
    color: "#199473",
    backgroundColor: "#dcfff4",
  },
  inactiveChip: {
    color: "#6b7280",
    backgroundColor: "#eef2f7",
  },
  detailPanel: {
    flex: 1,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 16,
    overflow: "hidden",
    backgroundColor: "#fff",
  },
  emptyList: {
    color: "#95a0b5",
    textAlign: "center",
    marginTop: theme.spacing(6),
    padding: theme.spacing(0, 2),
  },
}));

const createDefaultQuery = () => ({
  Limit: ModelUriQueryString.Limit,
  Page: ModelUriQueryString.Page,
  OrderBy: ModelUriQueryString.OrderBy,
  WhereGroupList: ModelUriQueryString.WhereGroupList,
  Exclude: ModelUriQueryString.Exclude,
  WhereRaw: ModelUriQueryString.WhereRaw,
  WhereLike: ModelUriQueryString.WhereLike,
  WhereLikeFields: ModelUriQueryString.WhereLikeFields,
});

const DepartmentCategory = (props) => {
  const translate = useTranslate();
  const { permissions, value, index } = props;
  const [error] = useState();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [uriQueryString, setUriQueryString] = useState(createDefaultQuery);
  const [searchText, setSearchText] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [mode, setMode] = useState("view");
  const [availableCount, setAvailableCount] = useState(0);
  const [availableDepts, setAvailableDepts] = useState([]);
  const classes = useStyles();
  let checkPermission = permissions?.find((i) => i.Name === permissionName["GL.DepartmentCategory"])?.View;

  const isInitialLoadRef = useRef(true);
  const fetchSearchList = useCallback(async (query, preferredId, nextMode) => {
    if (isInitialLoadRef.current) {
      setLoading(true);
    }
    const { Categories, AvailableDepartments } = await getCategorySetupInitialData(query);
    const nextItems = Categories?.Data || [];
    const unassignedDepts = AvailableDepartments || [];

    setCategories(nextItems);
    setAvailableDepts(unassignedDepts);
    setAvailableCount(unassignedDepts.length);
    setUriQueryString(query);

    if (typeof nextMode === "string") {
      setMode(nextMode);
    }

    if (preferredId === 0) {
      setSelectedId(0);
    } else if (preferredId) {
      const selectedItem = nextItems.find(
        (item) => Number(item.Id) === Number(preferredId),
      );
      setSelectedId(selectedItem ? selectedItem.Id : nextItems[0]?.Id ?? null);
    } else if (!nextItems.length) {
      setSelectedId(0);
      setMode("add");
    } else {
      setSelectedId((currentId) => {
        const hasCurrent = nextItems.some(
          (item) => Number(item.Id) === Number(currentId),
        );
        return hasCurrent ? currentId : (nextItems[0]?.Id ?? null);
      });
      setMode((currentMode) => (currentMode === "add" ? currentMode : "view"));
    }

    if (isInitialLoadRef.current) {
      setLoading(false);
      isInitialLoadRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (value === index && checkPermission) {
      fetchSearchList(createDefaultQuery());
    }
  }, [value, index, checkPermission, fetchSearchList]);

  const handleSearchChange = (event) => {
    const nextSearchText = event.target.value;
    setSearchText(nextSearchText);
    const nextQuery = {
      ...uriQueryString,
      Page: 1,
      WhereLike: nextSearchText ? `%${nextSearchText}%` : "",
      WhereLikeFields: nextSearchText
        ? ["DeptCateCode", "Description"]
        : [],
    };
    fetchSearchList(nextQuery);
  };

  const handleSelectCategory = (id) => {
    setSelectedId(id);
    setMode("view");
  };

  const handleAddCategory = () => {
    setSelectedId(0);
    setMode("add");
  };

  const handleDetailClose = (result) => {
    if (result === "delete") {
      fetchSearchList(uriQueryString, 0, "add");
      return;
    }

    if (result) {
      const numericId = Number(result);
      const targetId = Number.isFinite(numericId) ? numericId : result;
      setSelectedId(targetId);
      fetchSearchList(uriQueryString, targetId, "view");
      return;
    }

    if (categories.length > 0) {
      setMode("view");
      return;
    }

    setSelectedId(0);
    setMode("add");
  };

  if (loading && categories.length === 0 && selectedId === null) {
    return (
      <div style={{ marginInline: "auto" }}>
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ marginInline: "auto" }}>
        <Error />
      </div>
    );
  }

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      className={classes.tabPanel}
    >
      {checkPermission && value === index ? (
        <div className={classes.container}>
          <Paper className={classes.listPanel} elevation={0}>
            <div className={classes.listHeader}>
              <Typography variant="h6" className={classes.listTitle}>
                {translate("ra.field.Category List")}
              </Typography>
              <IconButton
                className={classes.addButton}
                onClick={handleAddCategory}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </div>

            <div className={classes.searchBox}>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                placeholder={translate("ra.field.Search groups...")}
                value={searchText}
                onChange={handleSearchChange}
                className={classes.searchInput}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: loading ? (
                    <InputAdornment position="end">
                      <CircularProgress size={16} />
                    </InputAdornment>
                  ) : null,
                }}
              />
            </div>

            <div className={classes.summaryBox}>
              <div className={classes.summaryItem}>
                <div className={classes.summaryValue}>
                  {categories.reduce((acc, cat) => acc + (cat.DeptCount || 0), 0)}
                </div>
                <div className={classes.summaryLabel}>
                  {translate("ra.field.Assigned")}
                </div>
              </div>
              <div className={classes.summaryItem}>
                <div className={classes.summaryValue}>{availableCount}</div>
                <div className={classes.summaryLabel}>
                  {translate("ra.field.Unassigned")}
                </div>
              </div>
            </div>

            <div className={classes.listBody}>
              {!categories.length ? (
                <Typography variant="body2" className={classes.emptyList}>
                  {translate("ra.info.notransaction")}
                </Typography>
              ) : (
                categories.map((item) => {
                  const isSelected =
                    mode !== "add" && Number(selectedId) === Number(item.Id);

                  return (
                    <button
                      type="button"
                      key={item.Id}
                      className={`${classes.listItem} ${
                        isSelected ? classes.listItemSelected : ""
                      }`}
                      onClick={() => handleSelectCategory(item.Id)}
                      aria-pressed={isSelected}
                    >
                      <div className={classes.itemCode}>{item.DeptCateCode}</div>
                      <div className={classes.itemTitleRow}>
                        <Typography variant="subtitle1" className={classes.itemTitle}>
                          {item.Description}
                        </Typography>
                        <Chip
                          size="small"
                          label={
                            item.Active
                              ? translate("ra.field.Active")
                              : translate("ra.field.In-Active")
                          }
                          className={`${classes.statusChip} ${
                            item.Active ? classes.activeChip : classes.inactiveChip
                          }`}
                        />
                      </div>
                      <Typography variant="body2" className={classes.itemSubtext}>
                        {item.DeptCount || 0} {translate("ra.field.Departments assigned")}
                      </Typography>
                    </button>
                  );
                })
              )}
            </div>
          </Paper>

          <Paper className={classes.detailPanel} elevation={0}>
            {selectedId !== null && selectedId !== undefined ? (
              <DetailPanel
                key={`${mode}-${selectedId}`}
                id={selectedId}
                title={translate("ra.module.Department Category")}
                mode={mode}
                setMode={setMode}
                open
                onClose={handleDetailClose}
                embedded
                availableDepts={availableDepts}
              />
            ) : null}
          </Paper>
        </div>
      ) : (
        <AccessDenied />
      )}
    </div>
  );
};

export default DepartmentCategory;
